import {css, StyleSheet} from 'aphrodite';
import React, {CSSProperties} from 'react';

import {Permissions} from 'auth/PermissionConstants';
import {ReviewBadgeContext} from 'notebook/reviews/ReviewBadgeContext';
import * as ReviewUtil from 'notebook/reviews/ReviewUtil';
import {GenericResponseJSON} from 'notifications';
import _RealtimeUpdateSubscriptionWrapper from 'realtime/RealtimeUpdateSubscriptionWrapper';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import HorizontalThrobber from 'ui-library/HorizontalThrobber';
import BenchlingIcon, {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {OngoingTaskResponse} from 'util/TaskRunner';

import ReviewActionButton from './ReviewActionButton';
import {Review, ReviewAction} from './ReviewConstants';
import {ReviewInProgressMenu} from './ReviewInProgressMenu';
import ReviewSummaryPanelMenu from './ReviewSummaryPanelMenu';
import {useReviewController} from './useReviewController';

export {REVIEWABLE_QUERY, wrapOnReviewChangeWithRefetch} from './useReviewController';

export interface DefaultBadgeSpec {
  text?: string;
  icon?: BenchlingIconName;
  style?: CSSProperties;
}

export interface ReviewBadgeProps {
  className?: string;
  reviewableId: string;
  enableReviewLifecycle?: boolean;
  defaultBadgeSpec?: DefaultBadgeSpec;
  onReviewChange?: (
    reviewableId: string,
    review: Review,
    filePermissions: Permissions,
    snapshotTask: OngoingTaskResponse | null
  ) => void;
  openHistory?: () => void;
  /** Custom rendering of additional info related to the review stage in modal */
  renderAdditionalReviewInfo?: (reviewableId: string, reviewAction: ReviewAction) => JSX.Element | null;
  /** Custom error rendering to show errors in modal. */
  renderReviewError?: (error: {responseJSON?: GenericResponseJSON}) => JSX.Element | null;
  getSendForReviewValidationError?: (reviewableId: string) => string | null;
  performanceMetadata?: {[key: string]: string}; // takes in performance metric
  unsubmittedTableNames?: Array<string>;
}

/**
 * Displays review status of a reviewable object.
 *
 * This is a thin view over {@link useReviewController}: the controller owns the reviewable query,
 * derivation, action wiring, and realtime refetch, while this component maps the returned data to the
 * badge button + dropdown presentation (text/icon/style via ReviewUtil and the optional defaultBadgeSpec).
 */
function ReviewBadge({
  reviewableId,
  enableReviewLifecycle,
  defaultBadgeSpec,
  onReviewChange,
  renderAdditionalReviewInfo,
  renderReviewError,
  getSendForReviewValidationError,
  performanceMetadata,
  openHistory,
  unsubmittedTableNames,
}: ReviewBadgeProps): JSX.Element | null {
  const controller = useReviewController(reviewableId, {
    enableReviewLifecycle,
    onReviewChange,
    openHistory,
    renderAdditionalReviewInfo,
    renderReviewError,
    getSendForReviewValidationError,
    performanceMetadata,
    unsubmittedTableNames,
  });

  if (controller.state === 'PENDING') {
    return (
      <div className={css(reviewStyles.throbber)}>
        <HorizontalThrobber />
      </div>
    );
  }

  if (controller.state === 'HIDDEN') {
    return null;
  }

  const {
    reviewStatus,
    terminalState,
    badgeStatus,
    isDropdown,
    showSummaryPanel,
    permissions,
    contextValue,
    dropdown,
    realtime,
  } = controller;

  const button = (
    <ReviewActionButton
      mode={badgeStatus}
      text={ReviewUtil.getStatusTextFromReview(
        reviewStatus,
        terminalState ?? undefined,
        defaultBadgeSpec?.text
      )}
      isDropdown={isDropdown}
      expanded={dropdown.isOpen}
      icon={
        <BenchlingIcon
          className="reviewBadge-icon"
          icon={ReviewUtil.getStatusIcon(reviewStatus, defaultBadgeSpec?.icon)}
        />
      }
      defaultBadgeStyle={defaultBadgeSpec?.style}
    />
  );

  let dropdownElement;
  if (isDropdown) {
    dropdownElement = (
      <Dropdown
        closeOnInsideClick={true}
        closeOnOutsideClick={true}
        isOpen={dropdown.isOpen}
        onToggle={dropdown.onToggle}
        menu={
          <ReviewBadgeContext.Provider value={contextValue}>
            {showSummaryPanel ? (
              <ReviewSummaryPanelMenu permissions={permissions} />
            ) : (
              <ReviewInProgressMenu
                disableOptions={dropdown.disableOptions}
                tooltipTitle={dropdown.tooltipTitle}
              />
            )}
          </ReviewBadgeContext.Provider>
        }
      >
        {button}
      </Dropdown>
    );
  }

  return (
    <ReviewBadgeContext.Provider value={contextValue}>
      <RealtimeUpdateSubscriptionWrapper
        apiId={realtime.apiId}
        refetch={realtime.refetch}
        messageFilter="no_filter"
      >
        {dropdownElement ?? button}
      </RealtimeUpdateSubscriptionWrapper>
    </ReviewBadgeContext.Provider>
  );
}

const reviewStyles = StyleSheet.create({
  throbber: {
    verticalAlign: 'middle',
    display: 'inline-flex',
    alignItems: 'center',
    marginLeft: 10,
  },
});

export default React.memo(ReviewBadge);

let RealtimeUpdateSubscriptionWrapper = _RealtimeUpdateSubscriptionWrapper;

export function mock(): void {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  function MockRealtimeUpdateSubscriptionWrapper(props: any): null | JSX.Element {
    return props.children;
  }
  RealtimeUpdateSubscriptionWrapper = MockRealtimeUpdateSubscriptionWrapper;
}

export function restore(): void {
  RealtimeUpdateSubscriptionWrapper = _RealtimeUpdateSubscriptionWrapper;
}
